import * as React from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

const textareaVariants = cva(
  cn(
    'flex w-full rounded-md px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground',
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary',
  ),
  {
    variants: {
      variant: {
        outlined: ['bg-background border', 'ea-textarea__outlined'],
        filled: ['bg-input border-transparent', 'ea-textarea__filled'],
      },
      rounded: {
        default: 'rounded-lg',
        none: 'rounded-none',
      },
    },
    defaultVariants: {
      rounded: 'default',
      variant: 'filled',
    },
  },
);

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  VariantProps<typeof textareaVariants> & {
    className?: string;
    classNames?: string;
  };

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, classNames, ...props }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    const adjustHeight = () => {
      const textarea = textareaRef.current;
      if (textarea) {
        // Reset height to recalculate
        textarea.style.height = 'auto';

        // Set new height
        const maxRows = 12; // Max 12 rows
        const lineHeight = 1.5 * 16; // Assuming 1.5rem line height and 16px font size
        const maxHeight = lineHeight * maxRows;

        const newHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = `${newHeight}px`;

        // Set overflow-y if content exceeds 12 rows
        textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
      }
    };

    React.useEffect(() => {
      adjustHeight(); // Adjust on mount
    }, []);

    return (
      <textarea
        ref={el => {
          textareaRef.current = el;
          if (ref) {
            if (typeof ref === 'function') ref(el);
            else (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
          }
        }}
        rows={5} // Base height
        style={{
          resize: 'none', // Prevent manual resize
          overflowY: 'hidden', // Initially hidden, becomes 'auto' when max reached
        }}
        className={cn(
          textareaVariants({
            variant: props.variant,
            rounded: props.rounded,
          }),
          'ea-textarea bg-input',
          props['aria-invalid'] && 'ea-textarea--invalid border-destructive text-destructive',
          className,
          classNames,
        )}
        onInput={adjustHeight}
        {...props}
      />
    );
  },
);

Textarea.displayName = 'Textarea';

export { Textarea };
